import React, { useState, useRef, useContext, useImperativeHandle, forwardRef, useEffect, useCallback, useMemo } from 'react';
import styles from './ProductIdentity.module.css';
import AuthContext from '../../../context/AuthContext';
import { mapRainforestDataToFormFields } from '../utils';

// Constants
const MAX_IMAGES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const NAVIGATION_DELAY = 300;

const ProductIdentity = forwardRef(({ 
  formData, 
  setFormData, 
  setExistingInput, 
  setFormStep,
  onImagesChange,
  imageError,
  initialImages = []
}, ref) => {
  const authCtx = useContext(AuthContext);
  const [description, setDescription] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadedImages, setUploadedImages] = useState(initialImages);
  
  const lastExtractedDescriptionRef = useRef('');
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Sync images with parent when navigating back to this step
  useEffect(() => {
    if (!initialImages?.length) {
      if (uploadedImages.length > 0) {
        setUploadedImages([]);
      }
      return;
    }

    const needsUpdate = uploadedImages.length !== initialImages.length ||
      uploadedImages.some((img, idx) => {
        const initialImg = initialImages[idx];
        return !initialImg || 
               img.previewUrl !== initialImg.previewUrl ||
               (img.file && initialImg.file && img.file !== initialImg.file);
      });
    
    if (needsUpdate) {
      setUploadedImages([...initialImages]);
    }
  }, [initialImages, uploadedImages.length]);

  // Helper to reset form data
  const resetFormData = useCallback(() => {
      const currentProjectName = formData?.projectName || '';
      const currentKeywords = formData?.keywords || '';
      const currentBannedKeywords = formData?.bannedKeywords || '';
      
      setFormData(prev => ({
        ...prev,
        projectName: currentProjectName,
        brandName: '',
        category: '',
        subCategory: '',
        productType: '',
        modelNumber: '',
        skuId: '',
        color: '',
        material: '',
        size: '',
        eanNumber: '',
        feature1: '',
        feature2: '',
        feature3: '',
        feature4: '',
        feature5: '',
        briefDescription: '',
        keywords: currentKeywords,
        bannedKeywords: currentBannedKeywords
      }));
      
    setExistingInput({
      title: '',
      description: '',
      bullets: ['', '', '', '', '']
    });
  }, [formData, setFormData, setExistingInput]);

  // Helper to update form data from mapped data
  const updateFormDataFromMapped = useCallback((mappedData, skipGeneration = false) => {
    setFormData(prev => ({
        ...prev,
      projectName: prev.projectName || '',
      brandName: mappedData.formData.brandName || '',
      category: mappedData.formData.category || '',
      subCategory: mappedData.formData.subCategory || '',
      productType: prev.productType || '',
      modelNumber: mappedData.formData.modelNumber || '',
      skuId: mappedData.formData.skuId || '',
      color: mappedData.formData.color || '',
      material: mappedData.formData.material || '',
      size: mappedData.formData.size || '',
      eanNumber: mappedData.formData.eanNumber || '',
      briefDescription: skipGeneration ? '' : (mappedData.formData.briefDescription || ''),
      feature1: skipGeneration ? '' : (mappedData.formData.feature1 || ''),
      feature2: skipGeneration ? '' : (mappedData.formData.feature2 || ''),
      feature3: skipGeneration ? '' : (mappedData.formData.feature3 || ''),
      feature4: skipGeneration ? '' : (mappedData.formData.feature4 || ''),
      feature5: skipGeneration ? '' : (mappedData.formData.feature5 || ''),
      application1: skipGeneration ? '' : (mappedData.formData.application1 || ''),
      application2: skipGeneration ? '' : (mappedData.formData.application2 || ''),
      keywords: prev.keywords || '',
      bannedKeywords: prev.bannedKeywords || ''
    }));

    if (mappedData.existingInput && !skipGeneration) {
      setExistingInput({
        title: mappedData.existingInput.title || '',
        description: mappedData.existingInput.description || '',
        bullets: mappedData.existingInput.bullets || ['', '', '', '', '']
      });
    } else if (skipGeneration) {
      setExistingInput({
        title: '',
        description: '',
        bullets: ['', '', '', '', '']
      });
    }
  }, [setFormData, setExistingInput]);

  const extractProductDetails = useCallback(async (shouldNavigate = false) => {
    const desc = description.trim();
    const lastExtracted = lastExtractedDescriptionRef.current;
    const descriptionChanged = lastExtracted !== desc && lastExtracted !== '';
    
    if (descriptionChanged) {
      resetFormData();
      lastExtractedDescriptionRef.current = '';
    } else if (lastExtracted === desc && !shouldNavigate) {
      return { success: true, alreadyExtracted: true };
    }

    setIsExtracting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/single-gen/extract-product-details`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx?.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ description: desc })
      });

      if (!response.ok) {
        return { success: false, error: 'Failed to extract product details' };
      }

        const result = await response.json();
      if (!result.success || !result.data) {
        return { success: false, error: 'Failed to extract product details' };
      }

      const mappedData = mapRainforestDataToFormFields(result.data);
      if (!mappedData) {
        return { success: false, error: 'Failed to map product data' };
      }

      updateFormDataFromMapped(mappedData, result.skip_generation);

      lastExtractedDescriptionRef.current = desc;

            if (shouldNavigate && setFormStep) {
        setTimeout(() => setFormStep(2), NAVIGATION_DELAY);
            }

      return { 
        success: true, 
        data: mappedData,
        skipGeneration: result.skip_generation 
      };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsExtracting(false);
    }
  }, [description, resetFormData, updateFormDataFromMapped, authCtx?.token, setFormStep]);

  useImperativeHandle(ref, () => ({
    extractProductDetails,
    isExtracting,
    getDescription: () => description.trim(),
    getUploadedImagesCount: () => uploadedImages.length
  }), [extractProductDetails, isExtracting, description, uploadedImages.length]);

  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = MAX_IMAGES - uploadedImages.length;

    if (remainingSlots <= 0) {
      e.target.value = '';
      return;
    }

    const newImages = files
      .slice(0, remainingSlots)
      .filter(file => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          return false;
        }
        if (file.size > MAX_FILE_SIZE) {
          return false;
        }
        return true;
      })
      .map(file => ({
        file,
        previewUrl: URL.createObjectURL(file)
      }));

    if (newImages.length > 0) {
      const updatedImages = [...uploadedImages, ...newImages];
      setUploadedImages(updatedImages);
      onImagesChange?.(updatedImages);

    }

    e.target.value = '';
  }, [uploadedImages, onImagesChange]);

  const handleRemoveImage = useCallback((index) => {
    const removed = uploadedImages[index];
    if (removed?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(removed.previewUrl);
    const updatedImages = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(updatedImages);
    onImagesChange?.(updatedImages);
  }, [uploadedImages, onImagesChange]);

  const handleTextareaChange = useCallback((e) => {
    const value = e.target.value;
    setDescription(value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  }, []);

  const handleTextareaKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  }, []);

  const handleAddImageClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [description]);

  // Memoized values
  const isMaxImagesReached = useMemo(() => uploadedImages.length >= MAX_IMAGES, [uploadedImages.length]);
  const imageCountText = useMemo(() => 
    uploadedImages.length > 0 ? `(${uploadedImages.length}/${MAX_IMAGES})` : '', 
    [uploadedImages.length]
  );

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <div className={styles.mainContent}>
            <div className={styles.descriptionInputContainer}>
              <div className={styles.descriptionInputCard}>
                <div className={styles.descriptionInputWrapper}>
                  <textarea
                    ref={textareaRef}
                    className={styles.descriptionInput}
                    placeholder="Describe your product here, add product images and media files"
                    value={description}
                    onChange={handleTextareaChange}
                    onKeyDown={handleTextareaKeyDown}
                    disabled={isExtracting}
                    rows={1}
                  />
                  <div className={styles.addButtonContainer}>
                    <button
                      type="button"
                      className={styles.addButton}
                      onClick={handleAddImageClick}
                      disabled={isExtracting || isMaxImagesReached}
                      title={isMaxImagesReached ? `Maximum ${MAX_IMAGES} images allowed` : 'Add images'}
                    > Add Images
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 0C5.33724 0 4.8 0.537264 4.8 1.2V4.8H1.2C0.537264 4.8 0 5.33723 0 6C0 6.66276 0.537264 7.2 1.2 7.2H4.8V10.8C4.8 11.4627 5.33724 12 6 12C6.66276 12 7.2 11.4627 7.2 10.8V7.2H10.8C11.4627 7.2 12 6.66276 12 6C12 5.33723 11.4627 4.8 10.8 4.8H7.2V1.2C7.2 0.537264 6.66276 0 6 0Z" fill="#2766AA"/>
                    </svg>
                    </button>
                    {/* <span className={styles.addButtonLabel}>Images {imageCountText}</span> */}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.helpSection}>
              <div className={styles.imageUploadContainer}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className={styles.fileInput}
                  id="product-image-upload"
                />

                {uploadedImages.length > 0 && (
                  <div className={styles.imagePreviewsContainer}>
                    {uploadedImages.map((img, index) => (
                      <div key={`${img.previewUrl}-${index}`} className={styles.imagePreviewItem}>
                        <img src={img.previewUrl} alt={`Preview ${index + 1}`} />
                        <div className={styles.imageActionButtons}>
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className={styles.removeImageButton}
                            title="Remove image"
                          >
                            <svg width="10" height="11" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.4441 2.87202H10.6505L10.2946 1.49558C10.0669 0.614518 9.25126 0 8.31244 0H4.68935C3.75048 0 2.93559 0.614518 2.70717 1.49558L2.35126 2.87202H0.557193C0.249337 2.87202 0 3.11298 0 3.41049C0 3.70799 0.249337 3.94895 0.557193 3.94895H0.78146L1.3881 12.1665C1.46401 13.1943 2.359 14 3.42608 14H9.57392C10.6409 14 11.5359 13.1943 11.6119 12.1665L12.2185 3.94895H12.4428C12.7507 3.94895 13 3.70799 13 3.41049C13 3.11298 12.7507 2.87202 12.4428 2.87202H12.4441ZM3.78877 1.75673C3.89254 1.35625 4.26308 1.07692 4.69003 1.07692H8.31311C8.74006 1.07692 9.1106 1.35692 9.21437 1.75673L9.50272 2.87202H3.49967L3.78802 1.75673H3.78877ZM10.5015 12.0898C10.4673 12.557 10.0599 12.9231 9.57513 12.9231H3.42658C2.94112 12.9231 2.53438 12.557 2.50025 12.0898L1.89848 3.949H11.1026L10.5008 12.0898H10.5015ZM7.42933 9.87208V6.28256C7.42933 5.98506 7.67866 5.7441 7.98652 5.7441C8.29437 5.7441 8.54371 5.98506 8.54371 6.28256V9.87208C8.54371 10.1696 8.29437 10.4105 7.98652 10.4105C7.67866 10.4105 7.42933 10.1696 7.42933 9.87208ZM4.45811 9.87208V6.28256C4.45811 5.98506 4.70745 5.7441 5.0153 5.7441C5.32316 5.7441 5.57249 5.98506 5.57249 6.28256V9.87208C5.57249 10.1696 5.32316 10.4105 5.0153 10.4105C4.70745 10.4105 4.45811 10.1696 4.45811 9.87208Z" fill="white"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {imageError && (
                  <div className={styles.imageErrorText}>
                    {imageError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ProductIdentity.displayName = 'ProductIdentity';

export default ProductIdentity;

